import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ThemedSelect.css';

/**
 * ThemedSelect — custom dropdown that replaces native <select>.
 * Renders a trigger pill + portal popover panel with themed items.
 *
 * Props:
 *  value        – current value
 *  onChange     – (value) => void
 *  options      – [{ value, label }]
 *  placeholder  – shown when nothing selected
 *  disabled     – disables trigger
 *  searchable   – show search field inside panel
 *  ariaLabel    – accessibility label
 *  className    – extra class on trigger
 *  minWidth     – override min-width on trigger
 */
export default function ThemedSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  searchable = false,
  ariaLabel,
  className = '',
  minWidth,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setOpen(false); setSearch(''); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, searchable]);

  const handleSelect = useCallback((val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const filtered = searchable && search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Position panel below trigger
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200),
    });
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`ts-trigger ${open ? 'ts-trigger--open' : ''} ${disabled ? 'ts-trigger--disabled' : ''} ${className}`}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={minWidth ? { minWidth } : undefined}
      >
        <span className="ts-trigger-label">{selectedLabel}</span>
        <svg className={`ts-chevron ${open ? 'ts-chevron--open' : ''}`} viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="ts-panel"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          role="listbox"
          aria-label={ariaLabel}
        >
          {searchable && (
            <div className="ts-panel-search">
              <input
                ref={searchRef}
                className="ts-search-input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}
          <div className="ts-panel-list">
            {filtered.length === 0 && (
              <div className="ts-empty">No results</div>
            )}
            {filtered.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`ts-item ${String(opt.value) === String(value) ? 'ts-item--selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={String(opt.value) === String(value)}
              >
                <span className="ts-item-label">{opt.label}</span>
                {String(opt.value) === String(value) && (
                  <svg className="ts-check" viewBox="0 0 16 16" fill="none">
                    <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
